import { DOCS_FORM_STATUS } from "core/consts";
import aiApi from "core/model/api/endpoints/extractDocs";
import { getErrorMessage } from "core/model/utils/errors";
import {
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStatusResponse,
} from "core/types";
import { useCallback, useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 2_000;

export const useDocsFillForm = ({
  auctionId,
  careseekerId,
  consultationId,
  externalId,
  onError,
  onPending,
  onSuccess,
  patientId,
  skip,
}: {
  careseekerId: number | undefined;
  patientId: number;
  auctionId: number | undefined;
  consultationId: number | undefined;
  skip: boolean;
  onSuccess: (response: DocsFormStatusResponse) => void;
  onError: (error: string) => void;
  onPending: () => void;
  externalId: string | undefined;
}) => {
  const [result, setResult] = useState<DocsFormStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const clearPollTimeout = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const handleFormStatus = useCallback(
    (
      formResult: DocsFormStatusResponse,
      handlers: {
        onFilling?: () => void;
        onFilled: () => void;
        onFailed: (message: string) => void;
      },
    ) => {
      const { onFailed, onFilled, onFilling } = handlers;

      switch (formResult.status) {
        case DOCS_FORM_STATUS.PENDING:
        case DOCS_FORM_STATUS.FILLING:
          onFilling?.();
          break;
        case DOCS_FORM_STATUS.FILLED:
          onFilled();
          break;
        case DOCS_FORM_STATUS.FAILED:
          onFailed(formResult.error_message);
          break;
      }
    },
    [],
  );

  const fetchFormResult = useCallback(
    async (formName: DocsFormName): Promise<DocsFormStatusResponse | null> => {
      try {
        if (!careseekerId || !patientId) {
          throw new Error("careseekerId or patientId is not defined");
        }

        if (!auctionId && !consultationId) {
          throw new Error("auctionId or consultationId is not defined");
        }

        const formResult = await aiApi.docs.fill({
          auctionId,
          careseekerId,
          consultationId,
          formName,
          patientId,
        });

        if (!isMountedRef.current) return null;

        setResult(formResult);
        setError(null);
        return formResult;
      } catch (err) {
        if (!isMountedRef.current) return null;

        const message = getErrorMessage(err);
        setError(message);
        onError(message);
        clearPollTimeout();
        return null;
      }
    },
    [auctionId, careseekerId, consultationId, patientId, onError],
  );

  const startPolling = useCallback(
    (formName: DocsFormName) => {
      const poll = async () => {
        const formResult = await fetchFormResult(formName);
        if (!formResult || !isMountedRef.current) {
          return;
        }

        handleFormStatus(formResult, {
          onFailed: (message) => {
            onError(message);
            clearPollTimeout();
          },
          onFilled: () => {
            onSuccess(formResult);
            clearPollTimeout();
          },
          onFilling: () => {
            pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          },
        });
      };

      poll();
    },
    [fetchFormResult, handleFormStatus, onError, onSuccess],
  );

  const fillFormAndPollForResult = useCallback(
    async ({
      formData,
      formName,
    }: {
      formName: DocsFormName;
      formData: DocsFormDataMapped<typeof formName> | undefined;
    }) => {
      if (skip) {
        return null;
      }

      if (!careseekerId || !patientId) {
        onError("careseekerId or patientId is not defined");
        return null;
      }

      if (!auctionId && !consultationId) {
        onError("auctionId or consultationId is not defined");
        return null;
      }

      clearPollTimeout();
      onPending();

      try {
        await aiApi.docs.start({
          auctionId,
          careseekerId,
          consultationId,
          externalId,
          formData,
          formName,
          patientId,
        });
      } catch (error) {
        const message = getErrorMessage(error);
        onError(`Failed to start form ${formName}: ${message}`);
        return null;
      }

      const formResult = await fetchFormResult(formName);
      if (!formResult || !isMountedRef.current) return null;

      handleFormStatus(formResult, {
        onFailed: (message) => {
          onError(message);
        },
        onFilled: () => {
          onSuccess(formResult);
        },
        onFilling: () => {
          startPolling(formName);
        },
      });

      return null;
    },
    [
      auctionId,
      consultationId,
      careseekerId,
      patientId,
      skip,
      onError,
      onPending,
      onSuccess,
      fetchFormResult,
      startPolling,
      handleFormStatus,
    ],
  );

  return {
    error,
    fetchFormResult,
    fillForm: fillFormAndPollForResult,
    resetResult: () => {
      setResult(null);
      setError(null);
    },
    result,
  };
};
